// src/components/files/RecentsView.tsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import FileItem from './FileItem';
import EmptyState from './EmptyState';

const RecentsView: React.FC = () => {
  const { t } = useTranslation();
  const { state, selectItem, isSelected } = useFileManager();
  const { recentDocuments, viewMode } = state;

  if (!recentDocuments || recentDocuments.length === 0) {
    return (
      <EmptyState
        icon={<HistoryIcon sx={{ fontSize: 80, color: 'grey.400' }} />}
        title={t('fileManager.recent.empty', 'Aucun fichier recent')}
        description={t('fileManager.recent.emptyDescription', 'Les fichiers que vous ouvrez ou modifiez apparaitront ici')}
      />
    );
  }

  const handleItemClick = (
    e: React.MouseEvent,
    id: number,
    type: 'folder' | 'document'
  ) => {
    selectItem({ id, type }, e.ctrlKey || e.metaKey, e.shiftKey);
  };

  // Group documents by date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);

  const todayDocs = recentDocuments.filter(doc => {
    const docDate = new Date(doc.updated_at || doc.created_at);
    return docDate >= today;
  });

  const yesterdayDocs = recentDocuments.filter(doc => {
    const docDate = new Date(doc.updated_at || doc.created_at);
    return docDate >= yesterday && docDate < today;
  });

  const lastWeekDocs = recentDocuments.filter(doc => {
    const docDate = new Date(doc.updated_at || doc.created_at);
    return docDate >= lastWeek && docDate < yesterday;
  });

  const olderDocs = recentDocuments.filter(doc => {
    const docDate = new Date(doc.updated_at || doc.created_at);
    return docDate < lastWeek;
  });

  const renderSection = (title: string, docs: typeof recentDocuments) => {
    if (docs.length === 0) return null;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase' }}>
          {title}
        </Typography>
        {viewMode === 'grid' ? (
          <Grid container spacing={2}>
            {docs.map((doc) => (
              <Grid item key={`doc-${doc.id}`}>
                <FileItem
                  type="document"
                  id={doc.id}
                  name={doc.title}
                  mimeType={doc.file_type}
                  fileSize={doc.file_size}
                  fileUrl={doc.file_url}
                  isFavorite={doc.is_favorite}
                  createdAt={doc.updated_at || doc.created_at}
                  isSelected={isSelected(doc.id, 'document')}
                  onClick={(e) => handleItemClick(e, doc.id, 'document')}
                  viewMode="grid"
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {docs.map((doc) => (
              <FileItem
                key={`doc-${doc.id}`}
                type="document"
                id={doc.id}
                name={doc.title}
                mimeType={doc.file_type}
                fileSize={doc.file_size}
                fileUrl={doc.file_url}
                isFavorite={doc.is_favorite}
                createdAt={doc.updated_at || doc.created_at}
                isSelected={isSelected(doc.id, 'document')}
                onClick={(e) => handleItemClick(e, doc.id, 'document')}
                viewMode="list"
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
        {t('fileManager.recent.title', 'Fichiers recents')}
      </Typography>

      {renderSection(t('fileManager.recent.today', "Aujourd'hui"), todayDocs)}
      {renderSection(t('fileManager.recent.yesterday', 'Hier'), yesterdayDocs)}
      {renderSection(t('fileManager.recent.lastWeek', 'Cette semaine'), lastWeekDocs)}
      {renderSection(t('fileManager.recent.older', 'Plus ancien'), olderDocs)}
    </Box>
  );
};

export default RecentsView;
